import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from '@/lib/axiosClient';
import {
  Activity, TrendingUp, RefreshCw, ShieldCheck, Banknote,
  ArrowUpRight, ArrowDownRight, CalendarClock, Layers, PieChart,
  AlertTriangle, Plus, Minus,
} from 'lucide-react';
import {
  Line, LineChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
  ReferenceArea, Label,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = '/api';

// The four momentum schemes, in display order, each with a fixed accent colour
// + line dash so they're distinguishable by shape, not colour alone (a11y).
const STRATS = [
  { id: 'v1', scheme: 'A', label: 'Core', color: '#60a5fa', dash: '0' },
  { id: 'v2', scheme: 'B', label: 'Growth', color: '#34d399', dash: '0' },
  { id: 'v3', scheme: 'C', label: 'Alpha', color: '#a78bfa', dash: '5 4' },
  { id: 'v4', scheme: 'D', label: 'Fundamental', color: '#f59e0b', dash: '5 4' },
];
const COLOR = Object.fromEntries(STRATS.map((s) => [s.id, s.color]));

const reduceMotion =
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const inr = (v, dp = 0) =>
  `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const pct = (v) => `${v >= 0 ? '+' : ''}${Number(v || 0).toFixed(2)}%`;
const shortDate = (d) => {
  try { return new Date(d).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }); }
  catch { return d; }
};
const monthLabel = (d) => {
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#16161f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontFamily: 'JetBrains Mono', marginBottom: 6 }}>
        {new Date(label).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
      </div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
          <span style={{ color: 'rgba(255,255,255,0.8)', minWidth: 52 }}>
            {STRATS.find((s) => s.id === p.dataKey)?.label}
          </span>
          <span style={{ color: '#f5f5f7', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {pct((p.value - 1) * 100)}<span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: 6 }}>{p.value.toFixed(2)}×</span>
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Strategies() {
  const [data, setData] = useState({});      // id -> portfolio
  const [logs, setLogs] = useState({});      // id -> rebalance logs
  const [loading, setLoading] = useState(true);
  const [rebalancing, setRebalancing] = useState(false);
  const [tab, setTab] = useState('v1');      // holdings deep-dive selector
  const [capInput, setCapInput] = useState({});  // id -> capital-adjust amount
  const [capBusy, setCapBusy] = useState(null);  // id currently submitting
  const [logScale, setLogScale] = useState(false);  // chart Y axis: linear % vs log multiple

  const fetchAll = useCallback(async () => {
    try {
      const results = await Promise.all(
        STRATS.flatMap((s) => [
          axios.get(`${API}/position/portfolio`, { params: { strategy: s.id } }),
          axios.get(`${API}/position/rebalance-log`, { params: { strategy: s.id, limit: 24 } }),
        ]),
      );
      const d = {}, l = {};
      STRATS.forEach((s, i) => { d[s.id] = results[i * 2].data; l[s.id] = results[i * 2 + 1].data.logs || []; });
      setData(d); setLogs(l);
    } catch (err) {
      console.error('Strategies fetch failed', err);
      toast.error('Unable to load strategies');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const doAdjust = async (id, sign, raw) => {
    setCapBusy(id);
    try {
      await axios.post(`${API}/position/capital`, null, { params: { strategy: id, amount: sign * raw } });
      toast.success(`${sign > 0 ? 'Added' : 'Withdrew'} ₹${raw.toLocaleString('en-IN')} on ${id.toUpperCase()}`);
      setCapInput((s) => ({ ...s, [id]: '' }));
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Capital update failed');
    } finally { setCapBusy(null); }
  };

  const requestAdjust = (id, sign) => {
    const raw = parseFloat(capInput[id]);
    if (!raw || raw <= 0) { toast.error('Enter an amount first'); return; }
    if (sign < 0) {   // withdrawals are confirmed — money out is semi-destructive
      const label = STRATS.find((s) => s.id === id)?.label;
      toast(`Withdraw ₹${raw.toLocaleString('en-IN')} from ${label}?`, {
        description: 'Available cash is taken now; any remainder is freed at the next rebalance.',
        action: { label: 'Withdraw', onClick: () => doAdjust(id, sign, raw) },
      });
    } else {
      doAdjust(id, sign, raw);
    }
  };

  const triggerRebalance = async () => {
    setRebalancing(true);
    try {
      await axios.post(`${API}/position/rebalance`);   // no strategy = all four
      toast.success('Rebalanced all strategies');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rebalance failed');
    } finally { setRebalancing(false); }
  };

  const cards = useMemo(() => STRATS.map((s) => {
    const p = data[s.id] || {};
    const equity = p.equity ?? p.cash ?? 0;
    const initial = p.initial_capital || 100000;
    const holdings = (p.holdings || []).length;
    const inCash = (logs[s.id]?.[0]?.in_cash) ?? (holdings === 0);
    const netContributed = p.net_contributed ?? initial;
    const profit = (p.profit != null) ? p.profit : (equity - netContributed);
    // Return is money-weighted (vs net capital contributed), so a deposit or
    // withdrawal is never mistaken for performance — matches the P&L line.
    const totalReturn = (equity / (netContributed || initial) - 1) * 100;
    return {
      ...s, meta: p, equity, initial, totalReturn, holdings, inCash, backtest: p.backtest || {},
      netContributed, profit,
      status: p.rebalance_status || 'complete', lastError: p.last_error,
      retryCount: p.retry_count || 0, retryDays: p.retry_days || 5,
      pending: p.pending_targets || [],
    };
  }), [data, logs]);

  // overlaid growth-multiple curves (equity / inception), merged on the date axis.
  // We store the MULTIPLE (always > 0) not the % so a log axis is valid even when
  // an early curve dips below its start; ticks/tooltip render it back as a %.
  const chart = useMemo(() => {
    const series = {};
    STRATS.forEach((s) => {
      const c = data[s.id]?.equity_curve || [];
      if (c.length >= 2) {
        const base = c[0].equity || 1;
        series[s.id] = Object.fromEntries(c.map((pt) => [pt.date, pt.equity / base]));
      }
    });
    const dates = [...new Set(STRATS.flatMap((s) => Object.keys(series[s.id] || {})))].sort();
    const rows = dates.map((date) => {
      const row = { date };
      STRATS.forEach((s) => { if (series[s.id]?.[date] != null) row[s.id] = series[s.id][date]; });
      return row;
    });
    const allVals = rows.flatMap((r) => STRATS.map((s) => r[s.id]).filter((v) => v != null));
    const minMult = allVals.length ? Math.min(...allVals) : 1;
    const maxMult = allVals.length ? Math.max(...allVals) : 1;
    // one tick per calendar month (avoids repeated "Feb 26 / Feb 26" labels)
    const seen = new Set();
    const monthTicks = dates.filter((d) => {
      const m = d.slice(0, 7);
      if (seen.has(m)) return false;
      seen.add(m); return true;
    });
    // when did the book go flat? = last day Core's curve moved (the regime exit
    // to cash). Everything after is the "in cash" period we shade as risk-off.
    let cashStart = null;
    for (let i = 1; i < rows.length; i++) {
      if (rows[i].v1 != null && rows[i - 1].v1 != null &&
          Math.abs(rows[i].v1 - rows[i - 1].v1) > 0.0005) cashStart = rows[i].date;
    }
    const cashEnd = dates[dates.length - 1];
    return { rows, points: rows.length, monthTicks, cashStart, cashEnd, minMult, maxMult };
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="strategies-loading">
        <Activity className="animate-spin" size={32} style={{ color: '#60a5fa' }} />
      </div>
    );
  }

  const anyInvested = cards.some((c) => !c.inCash);
  const tabHoldings = (data[tab]?.holdings || [])
    .map((h) => {
      const last = h.last_price ?? h.avg_price ?? 0;
      const mv = h.quantity * last;
      const upl = (last - (h.avg_price ?? last)) * h.quantity;
      const uplPct = h.avg_price ? ((last - h.avg_price) / h.avg_price) * 100 : 0;
      return { ...h, last, mv, upl, uplPct };
    })
    .sort((a, b) => b.mv - a.mv);
  const tabTargets = logs[tab]?.[0]?.target_symbols || [];

  return (
    <div className="space-y-6" data-testid="strategies-container">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="page-title">Strategies</h1>
          <p className="page-subtitle">
            Four momentum schemes tracked side by side
            <span className="mx-2 text-white/20">·</span>
            <span className={anyInvested ? 'text-profit' : 'text-info'}>
              {anyInvested ? 'Risk-on' : 'Risk-off (cash)'}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="mode-badge paper">Paper</span>
          <Button onClick={triggerRebalance} disabled={rebalancing} size="sm" data-testid="rebalance-all-btn">
            <RefreshCw size={14} className={rebalancing ? 'animate-spin' : ''} />
            <span className="ml-1.5">{rebalancing ? 'Rebalancing…' : 'Rebalance all'}</span>
          </Button>
        </div>
      </div>

      {/* Strategy cards */}
      <div className="strat-grid">
        {cards.map((c) => (
          <div key={c.id} className="strat-card" style={{ '--accent': c.color }}>
            <div className="strat-accent" />
            <div className="strat-card-head">
              <div className="flex items-center gap-2">
                <span className="strat-dot" style={{ background: c.color }} />
                <span className="strat-scheme">Scheme {c.scheme}</span>
                <span className="strat-name">{c.label}</span>
              </div>
              <span className={`role-chip ${c.meta.is_challenger ? 'challenger' : 'production'}`}>
                {c.meta.is_challenger ? 'Challenger' : 'Candidate'}
              </span>
            </div>
            <p className="strat-spec">{c.meta.description}</p>

            {c.status !== 'complete' && (
              <div style={{
                display: 'flex', gap: 8, alignItems: 'flex-start', margin: '4px 0 8px',
                padding: '8px 10px', borderRadius: 8, fontSize: 12,
                background: c.status === 'failed' ? 'rgba(248,113,113,0.12)' : 'rgba(245,158,11,0.12)',
                border: `1px solid ${c.status === 'failed' ? 'rgba(248,113,113,0.4)' : 'rgba(245,158,11,0.4)'}`,
                color: c.status === 'failed' ? '#fca5a5' : '#fbbf24',
              }}>
                <AlertTriangle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
                <div>
                  <strong>{c.status === 'failed'
                    ? 'Rebalance failed — buys unfilled'
                    : `Retrying buys · day ${c.retryCount}/${c.retryDays}`}</strong>
                  {c.pending.length > 0 && (
                    <span> · pending {c.pending.map((p) => `${p.symbol}×${p.quantity}`).join(', ')}</span>
                  )}
                  {c.lastError && (
                    <div style={{ marginTop: 3, color: 'rgba(255,255,255,0.6)', fontFamily: 'JetBrains Mono', fontSize: 11 }}>
                      {c.lastError}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="strat-hero">
              <span className="strat-hero-val" style={{ color: c.color }}>
                {c.backtest.cagr != null ? `${c.backtest.cagr}%` : '—'}
              </span>
              <span className="strat-hero-lbl">backtest CAGR · {c.backtest.basis || ''}</span>
            </div>

            <div className="strat-metrics">
              <div><span className="mlbl">Calmar</span><span className="mval">{c.backtest.calmar != null ? Number(c.backtest.calmar).toFixed(2) : '—'}</span></div>
              <div><span className="mlbl">Max DD</span><span className="mval text-loss">{c.backtest.maxdd != null ? `${c.backtest.maxdd}%` : '—'}</span></div>
              <div title={c.meta.exit_rank
                ? `Top ${c.meta.top_n} · ranking buffer: a holding is sold only once its momentum rank slips past ${c.meta.exit_rank} (cuts turnover)`
                : `Top ${c.meta.top_n} · full rotation: sell anything outside Top-${c.meta.top_n}`}>
                <span className="mlbl">Holds</span>
                <span className="mval">{c.meta.top_n}{c.meta.exit_rank ? <span className="text-white/40"> · hold ≤{c.meta.exit_rank}</span> : null}</span>
              </div>
            </div>

            <div className="strat-live">
              <div>
                <span className="mlbl">Paper equity</span>
                <span className="mval">{inr(c.equity)}</span>
              </div>
              <div className="text-right">
                <span className="mlbl">Return</span>
                <span className={`mval ${c.totalReturn >= 0 ? 'text-profit' : 'text-loss'}`}>{pct(c.totalReturn)}</span>
              </div>
              <span className={`status-pill ${c.inCash ? 'cash' : 'invested'}`}>
                {c.inCash ? <Banknote size={12} /> : <ShieldCheck size={12} />}
                {c.inCash ? 'In cash' : `${c.holdings} held`}
              </span>
            </div>

            {/* Feature 1 — capital control + money-weighted P&L */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 8, marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                Invested {inr(c.netContributed)} · P&L{' '}
                <span className={c.profit >= 0 ? 'text-profit' : 'text-loss'}>{inr(c.profit)}</span>
                {c.meta.pending_withdrawal > 0 && (
                  <span style={{ color: '#fbbf24' }}> · −{inr(c.meta.pending_withdrawal)} queued</span>
                )}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="number" min="0" placeholder="₹ amount"
                  aria-label={`Capital amount for ${c.label}`}
                  value={capInput[c.id] || ''} disabled={capBusy === c.id}
                  onChange={(e) => setCapInput((s) => ({ ...s, [c.id]: e.target.value }))}
                  style={{ width: 88, padding: '4px 6px', fontSize: 12, borderRadius: 6,
                    border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)',
                    color: '#f5f5f7' }} />
                <button onClick={() => requestAdjust(c.id, 1)} title="Add capital"
                  aria-label={`Add capital to ${c.label}`} disabled={capBusy === c.id}
                  style={{ display: 'grid', placeItems: 'center', width: 26, height: 26, borderRadius: 6,
                    border: '1px solid rgba(52,211,153,0.4)', background: 'rgba(52,211,153,0.12)',
                    color: '#34d399', cursor: capBusy === c.id ? 'wait' : 'pointer', opacity: capBusy === c.id ? 0.5 : 1 }}>
                  <Plus size={14} />
                </button>
                <button onClick={() => requestAdjust(c.id, -1)} title="Withdraw capital"
                  aria-label={`Withdraw capital from ${c.label}`} disabled={capBusy === c.id}
                  style={{ display: 'grid', placeItems: 'center', width: 26, height: 26, borderRadius: 6,
                    border: '1px solid rgba(248,113,113,0.4)', background: 'rgba(248,113,113,0.12)',
                    color: '#f87171', cursor: capBusy === c.id ? 'wait' : 'pointer', opacity: capBusy === c.id ? 0.5 : 1 }}>
                  <Minus size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Combined performance chart */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title flex items-center gap-2"><TrendingUp size={16} className="text-info" /> Live Performance</h2>
          <div className="flex items-center gap-3">
            <div className="chart-legend">
              {STRATS.map((s) => (
                <span key={s.id} className="leg-item">
                  <span className="leg-line" style={{ background: s.color, borderBottom: s.dash !== '0' ? `2px dashed ${s.color}` : 'none' }} />
                  {s.label}
                </span>
              ))}
            </div>
            <div role="radiogroup" aria-label="Chart scale" style={{ display: 'flex', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7, overflow: 'hidden' }}>
              {[['Linear', false], ['Log', true]].map(([lbl, val]) => (
                <button key={lbl} role="radio" aria-checked={logScale === val}
                  onClick={() => setLogScale(val)}
                  title={val ? 'Log scale — compare relative growth across schemes of very different size' : 'Linear % return'}
                  style={{
                    padding: '3px 10px', fontSize: 11, fontFamily: 'JetBrains Mono', cursor: 'pointer',
                    border: 'none', color: logScale === val ? '#f5f5f7' : 'rgba(255,255,255,0.45)',
                    background: logScale === val ? 'rgba(255,255,255,0.10)' : 'transparent',
                  }}>{lbl}</button>
              ))}
            </div>
          </div>
        </div>
        {chart.points < 4 ? (
          <div className="py-14 flex flex-col items-center gap-2 text-white/45 text-sm">
            <Activity size={28} className="text-white/25" />
            Paper curves build after a few rebalances.
            <span className="text-white/35">Backtest track records are shown in the cards above.</span>
          </div>
        ) : (
          <>
            <p className="sr-only">Cumulative paper return for Core, Growth, Alpha and Fundamental since inception.</p>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chart.rows} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                {chart.cashStart && (
                  <ReferenceArea x1={chart.cashStart} x2={chart.cashEnd} fill="rgba(96,165,250,0.06)" stroke="none">
                    <Label value="risk-off · in cash" position="insideTopRight" fill="rgba(255,255,255,0.3)"
                      fontSize={11} style={{ fontFamily: 'JetBrains Mono' }} />
                  </ReferenceArea>
                )}
                <XAxis dataKey="date" ticks={chart.monthTicks}
                  tickFormatter={(d) => new Date(d).toLocaleDateString('en-IN', { month: 'short' })}
                  stroke="rgba(255,255,255,0.4)" tickLine={false} axisLine={false}
                  style={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                <YAxis width={48} stroke="rgba(255,255,255,0.4)" tickLine={false} axisLine={false}
                  scale={logScale ? 'log' : 'linear'}
                  domain={logScale ? [Math.max(chart.minMult * 0.95, 0.01), chart.maxMult * 1.05] : ['auto', 'auto']}
                  allowDataOverflow={logScale}
                  ticks={logScale ? [0.5, 1, 2, 3, 5, 8].filter((t) => t >= chart.minMult * 0.9 && t <= chart.maxMult * 1.1) : undefined}
                  tickFormatter={(v) => {
                    const r = (v - 1) * 100;
                    return `${r >= 0 ? '+' : ''}${r.toFixed(0)}%`;
                  }}
                  style={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.15)' }} />
                {STRATS.map((s) => (
                  <Line key={s.id} type="monotone" dataKey={s.id} stroke={s.color} strokeWidth={2}
                    strokeDasharray={s.dash} dot={false} connectNulls isAnimationActive={!reduceMotion} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </div>

      {/* At-a-glance comparison */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title flex items-center gap-2"><Layers size={16} className="text-info" /> At a Glance</h2>
          <span className="card-eyebrow">backtest vs live</span>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Strategy</th><th>Universe</th><th>Signal</th><th>Rotation</th>
                <th className="text-right">CAGR</th><th className="text-right">Calmar</th><th className="text-right">Max DD</th>
                <th className="text-right">Equity</th><th className="text-right">Return</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {cards.map((c) => (
                <tr key={c.id}>
                  <td>
                    <span className="flex items-center gap-2">
                      <span className="strat-dot" style={{ background: c.color }} />
                      <span className="font-semibold">{c.label}</span>
                      <span className="text-white/35 text-xs font-mono">{c.scheme}</span>
                    </span>
                  </td>
                  <td className="text-white/65">{c.meta.universe === 'top200' ? 'Top-200' : 'Top-100'}</td>
                  <td className="text-white/65">
                    {c.id === 'v1' ? '6M momentum'
                      : (c.id === 'v2' || c.id === 'v4') ? '6M+12M blend'
                      : '12-1 skip-month'}
                    {c.id === 'v4' ? ' + Q/V tilt' : ''}
                  </td>
                  <td className="text-white/65" title={c.meta.exit_rank
                    ? `Ranking buffer: hold while rank ≤ ${c.meta.exit_rank}, sell only below it`
                    : `Full rotation: sell anything outside Top-${c.meta.top_n}`}>
                    {c.meta.exit_rank ? `hold ≤${c.meta.exit_rank}` : 'full'}
                  </td>
                  <td className="text-right font-mono tabular" style={{ color: c.color }}>{c.backtest.cagr}%</td>
                  <td className="text-right font-mono tabular">{c.backtest.calmar != null ? Number(c.backtest.calmar).toFixed(2) : '—'}</td>
                  <td className="text-right font-mono tabular text-loss">{c.backtest.maxdd}%</td>
                  <td className="text-right font-mono tabular">{inr(c.equity)}</td>
                  <td className={`text-right font-mono tabular ${c.totalReturn >= 0 ? 'text-profit' : 'text-loss'}`}>{pct(c.totalReturn)}</td>
                  <td>
                    <span className={`badge ${c.inCash ? 'badge-open' : 'badge-buy'}`}>{c.inCash ? 'Cash' : 'Invested'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Holdings deep-dive (segmented by strategy) */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title flex items-center gap-2"><PieChart size={16} className="text-info" /> Holdings</h2>
          <div className="seg" role="tablist" aria-label="Select strategy holdings">
            {STRATS.map((s) => (
              <button key={s.id} role="tab" aria-selected={tab === s.id}
                className={`seg-btn ${tab === s.id ? 'active' : ''}`} onClick={() => setTab(s.id)}
                style={tab === s.id ? { '--accent': s.color } : undefined}>
                <span className="strat-dot" style={{ background: s.color }} />{s.label}
              </button>
            ))}
          </div>
        </div>

        {tabHoldings.length === 0 ? (
          <div className="py-12 flex flex-col items-center gap-2 text-white/45 text-sm" data-testid="no-holdings">
            <Banknote size={26} className="text-white/25" />
            {(logs[tab]?.[0]?.in_cash)
              ? 'In cash — Nifty is below its 200-EMA, so the regime gate is holding cash. Positions appear when the trend turns risk-on.'
              : 'No holdings yet. Run a rebalance to enter.'}
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Symbol</th><th className="text-right">Qty</th><th className="text-right">Avg</th>
                  <th className="text-right">Last</th><th className="text-right">Value</th><th className="text-right">Unrealized P&amp;L</th>
                </tr>
              </thead>
              <tbody>
                {tabHoldings.map((h) => (
                  <tr key={h.symbol}>
                    <td className="font-semibold">{h.symbol}</td>
                    <td className="text-right font-mono tabular">{h.quantity}</td>
                    <td className="text-right font-mono tabular">{inr(h.avg_price, 2)}</td>
                    <td className="text-right font-mono tabular">{inr(h.last, 2)}</td>
                    <td className="text-right font-mono tabular">{inr(h.mv)}</td>
                    <td className="text-right">
                      <span className={`inline-flex items-center justify-end gap-1 font-mono font-semibold tabular ${h.upl >= 0 ? 'text-profit' : 'text-loss'}`}>
                        {h.upl >= 0 ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
                        {inr(Math.abs(h.upl))} ({pct(h.uplPct)})
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tabTargets.length > 0 && (
          <div className="mt-5">
            <div className="flex items-center gap-2 mb-2.5 text-white/45 text-xs font-mono uppercase tracking-wider">
              <CalendarClock size={13} /> Current ranking · {logs[tab][0].as_of}
            </div>
            <div className="flex flex-wrap gap-2">
              {tabTargets.map((sym, i) => (
                <span key={sym} className="rank-chip">
                  <span className="font-mono text-[11px] text-white/40">{i + 1}</span>
                  <span className="font-semibold text-white/90">{sym}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Rebalance history — the monthly trades (buys/sells) */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title flex items-center gap-2">
            <CalendarClock size={16} className="text-info" /> Rebalance History
          </h2>
          <span className="card-eyebrow">{STRATS.find((s) => s.id === tab)?.label} · monthly</span>
        </div>
        {(logs[tab] || []).length === 0 ? (
          <div className="py-10 text-center text-white/40 text-sm">No rebalances recorded yet.</div>
        ) : (
          <div className="rebal-list">
            {(logs[tab] || []).map((log) => {
              const buys = (log.orders || []).filter((o) => o.side === 'BUY');
              const sells = (log.orders || []).filter((o) => o.side === 'SELL');
              const chg = (log.equity_after && log.equity_before)
                ? (log.equity_after / log.equity_before - 1) * 100 : null;
              return (
                <div key={log.as_of} className="rebal-row">
                  <div className="rebal-meta">
                    <span className="rebal-date">{monthLabel(log.as_of)}</span>
                    <span className={`badge ${log.in_cash ? 'badge-open' : 'badge-buy'}`}>
                      {log.in_cash ? 'Cash · risk-off' : `Invested · ${(log.target_symbols?.length || buys.length)}`}
                    </span>
                    {chg != null && (
                      <span className={`font-mono text-xs ${chg >= 0 ? 'text-profit' : 'text-loss'}`}>{pct(chg)}</span>
                    )}
                  </div>
                  <div className="rebal-orders">
                    {buys.length > 0 && (
                      <div className="ord-line">
                        <span className="ord-tag buy">BUY</span>
                        {buys.map((o) => <span key={`b${o.symbol}`} className="ord-chip">{o.symbol}</span>)}
                      </div>
                    )}
                    {sells.length > 0 && (
                      <div className="ord-line">
                        <span className="ord-tag sell">SELL</span>
                        {sells.map((o) => <span key={`s${o.symbol}`} className="ord-chip">{o.symbol}</span>)}
                      </div>
                    )}
                    {buys.length === 0 && sells.length === 0 && (
                      <span className="text-white/35 text-sm">Held cash — no trades this month.</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
